import { StateCreator } from 'zustand';
import { AppState, Snapshot } from '../types';
import { saveToLocalStorage, generateSimpleId, forceNumber } from '../utils';
import { getNewPast, safeClone, sanitizeClientData, syncParamsToGlobal, injectSalonDefizit, defaultULHalbjahr1, defaultULHalbjahr2 } from './coreDataSlice';
import { resolveEffectiveClientMwstSatz } from './settingsSlice';

export type RolloverSliceType = Pick<AppState, 
    | 'savedSnapshots' | 'captureState' | 'createSnapshot' | 'loadSnapshot' | 'deleteSnapshot'
> & {
    executeJahreswechsel: () => void;
    rolloverConfirm: boolean;
    setRolloverConfirm: (val: boolean) => void;
    addNewYear: (jahr: number, bwaUrl: string, snapshotKurzauswertung?: any) => void;
    updateYearBwaUrl: (jahr: number, bwaUrl: string) => void; 
};

export const createRolloverSlice: StateCreator<AppState, [], [], RolloverSliceType> = (set, get) => ({
    savedSnapshots: [],
    
    rolloverConfirm: false,
    
    setRolloverConfirm: (val) => set((state) => {
        if (state.rolloverConfirm) return state; 
        return { rolloverConfirm: val };
    }),

    captureState: () => set(state => {
        const lastPast = state.past[state.past.length - 1];
        const currentSnapshot = { clientData: state.clientData, settings: state.settings };
        if (lastPast && JSON.stringify(lastPast) === JSON.stringify(currentSnapshot)) return state;
        const newPast = getNewPast(state);
        return { past: newPast, future: [] }; 
    }),

    createSnapshot: (name: string) => set(state => {
        const newSnapshot: Snapshot = { id: generateSimpleId(), name, timestamp: Date.now(), data: safeClone(state.clientData) };
        const newSnapshots = [newSnapshot, ...state.savedSnapshots];
        saveToLocalStorage({ ...state, savedSnapshots: newSnapshots });
        return { savedSnapshots: newSnapshots, isDirty: true };
    }),

    loadSnapshot: (id: string) => set(state => {
        const target = state.savedSnapshots.find(s => s.id === id);
        if (!target) return state;
        const newPast = getNewPast(state);

        const rawData = (target.data as any)?.clientData ?? target.data;
        const loadedData = safeClone(rawData);

        delete loadedData.settings;
        delete loadedData.team;
        delete loadedData.savedSnapshots;
        delete loadedData.appSettings;

        const safeLoadedData = sanitizeClientData(loadedData);
        const settings = syncParamsToGlobal(safeLoadedData, get().settings);
        const finalData = injectSalonDefizit(safeLoadedData, settings);

        saveToLocalStorage({ ...state, clientData: finalData, settings });
        return { past: newPast, future: [], clientData: finalData, settings, isDirty: true };
    }),

    deleteSnapshot: (id: string) => set(state => {
        const newSnapshots = state.savedSnapshots.filter(s => s.id !== id);
        saveToLocalStorage({ ...state, savedSnapshots: newSnapshots });
        return { savedSnapshots: newSnapshots, isDirty: true };
    }),

    addNewYear: (jahr, bwaUrl, snapshotKurzauswertung) => set(state => {
        localStorage.setItem('CURRENT_YEAR', jahr.toString());
        sessionStorage.setItem('CURRENT_YEAR', jahr.toString());
        localStorage.setItem('CURRENT_YEAR_STATUS', 'datenerfassung');
        window.dispatchEvent(new Event('storage'));

        const newPast = getNewPast(state);
        const currentClientData = safeClone(state.clientData);

        const hj1 = currentClientData.ulHalbjahr1 || [];
        const hj2 = currentClientData.ulHalbjahr2 || [];
        let sumUmsatz = 0;
        let activeMonthsCount = 0;
        [...hj1, ...hj2].forEach((monat: any) => {
            const umsatz = forceNumber(monat.bedienumsatzBrutto) + forceNumber(monat.vkUmsatzBrutto);
            if (umsatz > 0) {
                sumUmsatz += umsatz;
                activeMonthsCount++;
            }
        });
        const avgUnternehmerUmsatz = activeMonthsCount > 0 ? (sumUmsatz / activeMonthsCount) : 0;

        // 💉 CHIRURGIE-FIX: MULTI-FOUNDER WASCHANLAGE (Sicherer Rollover)
        let washedPersonengesellschaft = { aktiv: false, partner: [] };
        if (currentClientData.personengesellschaft && currentClientData.personengesellschaft.aktiv) {
            washedPersonengesellschaft = {
                aktiv: true,
                partner: (currentClientData.personengesellschaft.partner || []).map((p: any) => {
                    // 1. Array bereinigen & echten Schnitt ermitteln (Teilen nur durch Monate > 0)
                    const oldUmsaetze = p.monatsUmsaetze || [];
                    const activeMonths = oldUmsaetze.filter((val: number) => forceNumber(val) > 0);
                    
                    const calculatedAvg = activeMonths.length > 0 
                        ? activeMonths.reduce((sum: number, val: number) => sum + forceNumber(val), 0) / activeMonths.length 
                        : (p.durchschnittsumsatz || 0);

                    // 2. Partner mit frischen Ist-Werten fürs neue Jahr zurückgeben
                    return {
                        ...p,
                        durchschnittsumsatz: calculatedAvg,
                        durchschnittsumsatzVJ: calculatedAvg, // Historischen Wert als Vorjahr fixieren
                        monatsUmsaetze: Array(12).fill(0)     // Array für das neue Jahr nullen!
                    };
                })
            };
        } else {
            washedPersonengesellschaft = currentClientData.personengesellschaft || { aktiv: false, partner: [] };
        }

        const mwstFaktor = 1 + resolveEffectiveClientMwstSatz(state.settings, currentClientData) / 100;
        const lfData = currentClientData.lohnfaktorData || {};
        const mindestumsatz1 = lfData.mindestumsatz1 || 0;
        const mindestumsatz2 = lfData.mindestumsatz2 || 0;
        const privatentnahme = (currentClientData.unternehmerData?.entnahme || 0) + (currentClientData.unternehmerData?.sozialkosten || 0);

        const newVorjahresDaten = (currentClientData.gesamtleistungData || []).map((monat: any, index: number) => {
            const gesamtBrutto = (forceNumber(monat.bedienumsatzBrutto) || 0) + (forceNumber(monat.vkUmsatzBrutto) || 0);
            if (gesamtBrutto === 0) return { vorjahrWert1: 0, vorjahrWert2: 0, vorjahrProzent: 0 };

            const snap = monat.frozenCalculations || {};
            const currentBaseMindestumsatz = index < 6 ? mindestumsatz1 : mindestumsatz2;
            const activeMindestumsatzBase = (monat.abgeschlossen && snap.mindestumsatzBase !== undefined) ? snap.mindestumsatzBase : currentBaseMindestumsatz;
            const activeMwstFaktor = (monat.abgeschlossen && snap.mwstFaktor !== undefined) ? snap.mwstFaktor : mwstFaktor;
            const activePrivatentnahme = (monat.abgeschlossen && snap.privatentnahme !== undefined) ? snap.privatentnahme : privatentnahme;

            const anpassungsFaktor = monat.kostenErhoeht ? -1 : 1;
            const angepassterMindestumsatz = activeMindestumsatzBase + ((forceNumber(monat.aussergewoehnlich) || 0) * anpassungsFaktor);

            const gesamtNetto = activeMwstFaktor ? gesamtBrutto / activeMwstFaktor : 0;
            const ruecklagenDelta = gesamtNetto - angepassterMindestumsatz;
            const gewinn = ruecklagenDelta + activePrivatentnahme;

            let performanceProzent = 0;
            if (angepassterMindestumsatz > 0) {
                performanceProzent = ruecklagenDelta / angepassterMindestumsatz;
            }

            return {
                vorjahrWert1: gesamtNetto,
                vorjahrWert2: gewinn,
                vorjahrProzent: performanceProzent 
            };
        });
        localStorage.setItem('pe_vorjahres_daten_v4', JSON.stringify(newVorjahresDaten));

        // =========================================================================
        // --- 📸 DAS FOTO & 🛡️ PERFEKTER VORJAHRES-EXTRAKTOR ---
        // =========================================================================
        const vorjahresBwaData = safeClone(currentClientData.bwaData || {});
        const vorjahresUnternehmerData = safeClone(currentClientData.unternehmerData || {});
        
        // Kugelsicheres Aufsummieren der echten Stamm-Arrays mit forceNumber
        const prodGrund = (currentClientData.personalData || []).reduce((s: number, m: any) => s + (forceNumber(m.grundgehalt) || 0), 0);
        const prodBoni = (currentClientData.personalData || []).reduce((s: number, m: any) => s + (forceNumber(m.bonus) || 0), 0);
        const prodZusatz = (currentClientData.personalData || []).reduce((s: number, m: any) => s + (forceNumber(m.lohnzusatz || m.lohnkostenzusatz) || 0), 0);

        const unprodGrund = (currentClientData.unproductivePersonalData || []).reduce((s: number, m: any) => s + (forceNumber(m.grundgehalt) || 0), 0);
        const unprodZusatz = (currentClientData.unproductivePersonalData || []).reduce((s: number, m: any) => s + (forceNumber(m.lohnzusatz || m.lohnkostenzusatz) || 0), 0);

        const azubiGrund = (currentClientData.azubiAssistenzData || []).reduce((s: number, m: any) => s + (forceNumber(m.grundgehalt) || 0), 0);
        const azubiZusatz = (currentClientData.azubiAssistenzData || []).reduce((s: number, m: any) => s + (forceNumber(m.lohnzusatz || m.lohnkostenzusatz) || 0), 0);

        const vorjahresPersonalData = [
            { id: "p_grund", vorjahrMonat: prodGrund },
            { id: "p_boni", vorjahrMonat: prodBoni },
            { id: "p_zusatz", vorjahrMonat: prodZusatz },
            { id: "u_grund", vorjahrMonat: unprodGrund },
            { id: "u_zusatz", vorjahrMonat: unprodZusatz },
            { id: "a_grund", vorjahrMonat: azubiGrund },
            { id: "a_zusatz", vorjahrMonat: azubiZusatz }
        ];
        // =========================================================================

        const newBwaData = { ...currentClientData.bwaData };
        const bwaFieldsToZero = [
            'umsatz', 'material', 'warenabgabe', 'raumkosten', 'betriebssteuern', 'versicherungen',
            'reparatur', 'kfzKosten', 'werbeReisekosten', 'zinsaufwendungen', 'abschreibungen',
            'sonstigeKosten', 'sonstigeEintrag1', 'sonstigeEintrag2', 'sonstigeEintrag3',
            'sonstigeEintrag4', 'kredite', 'zinsenMiete', 'gewerbesteuer', 'koerperschaftssteuer',
            'geraetEintrag1', 'geraetEintrag2', 'geraetEintrag3', 'geraetEintrag4', 'geraetEintrag5', 'geraetEintrag6',
            'besondereKosten', 'toolAdd1', 'toolAdd2', 'budgetPlansumme', 'toolJahrMonat', 'toolMonatJahr',
            'toolBruttoNetto', 'toolNettoBrutto', 'preisExpert', 'prognoseBruttolohn'
        ];
        bwaFieldsToZero.forEach(f => { (newBwaData as any)[f] = 0; });

        const resetLohnfaktor = { liquiditaetsaufschlag: 0, sicherheitsfaktor: 0, manualLohnfaktor: 0, kostendeckungsfaktor1: 0, kostendeckungsfaktor2: 0, mindestumsatz1: 0, mindestumsatz2: 0, unternehmerStundenlohn_editable: 0 };

        const newMitarbeiterLeistung = safeClone(currentClientData.mitarbeiterLeistung);
        Object.keys(newMitarbeiterLeistung).forEach(empId => {
            const emp = newMitarbeiterLeistung[empId];
            if (emp) {
                emp.halbjahr1 = defaultULHalbjahr1(); emp.halbjahr2 = defaultULHalbjahr2();
                if (emp.monatsEingaben) { emp.monatsEingaben.halbjahr1 = defaultULHalbjahr1(); emp.monatsEingaben.halbjahr2 = defaultULHalbjahr2(); }
                delete emp.lockedMetrics; 
            }
        });

        const newKalkulator = safeClone(currentClientData.kalkulator);
        if (newKalkulator.inputs) { newKalkulator.inputs.bisherigerPreis = newKalkulator.inputs.neuerPreis || 0; newKalkulator.inputs.materialPreis = 0; newKalkulator.inputs.materialGramm = 0; }

        const newGesamtleistungData = (currentClientData.gesamtleistungData || []).map((m: any, index: number) => {
            const newM = { 
                ...m, 
                bedienumsatzBrutto: 0, 
                vkUmsatzBrutto: 0, 
                aussergewoehnlich: 0, 
                kostenErhoeht: false, 
                abgeschlossen: false 
            };
            
            delete newM.frozenCalculations;
            delete newM.lockedSnapshot;

            newM.vorjahrWert1 = newVorjahresDaten[index] ? newVorjahresDaten[index].vorjahrWert1 : 0;
            newM.vorjahrWert2 = newVorjahresDaten[index] ? newVorjahresDaten[index].vorjahrWert2 : 0;
            newM.vorjahrProzent = newVorjahresDaten[index] ? newVorjahresDaten[index].vorjahrProzent : 0;
            return newM;
        });

        const newUrlaubsverlustData = (currentClientData.urlaubsverlustData || []).map((m: any) => ({ ...m, urlaub: 0, krankheit: 0 }));

        const jahrSettingsSnapshot = safeClone(state.settings);

        const currentYears = currentClientData.geschaeftsjahre || [];
        const updatedJahre = [...currentYears, { 
            jahr, 
            status: 'datenerfassung', 
            bwaUrl,
            jahrSettings: jahrSettingsSnapshot 
        }];
        
        const unlockedDrafts = safeClone(currentClientData.drafts || { existingStaff: {}, newStaff: [], entrepreneur: [], costs: [], clientMaster: [], openingHours: [] });
        
        const unlockAll = (arr: any[]) => (arr || []).forEach(item => { 
            if (item) { 
                item.isLocked = false; 
                delete item.lockedBy; 
                delete item.lockedAt; 
            } 
        });
        
        if (unlockedDrafts.existingStaff) {
            Object.values(unlockedDrafts.existingStaff).forEach((item: any) => { 
                if (item) { 
                    item.isLocked = false; 
                    delete item.lockedBy; 
                    delete item.lockedAt; 
                } 
            });
        }
        unlockAll(unlockedDrafts.newStaff); 
        unlockAll(unlockedDrafts.entrepreneur); 
        unlockAll(unlockedDrafts.clientMaster); 
        unlockAll(unlockedDrafts.costs); 
        unlockAll(unlockedDrafts.openingHours);

        const newClientData = {
            ...currentClientData, 
            
            aktivesJahr: jahr.toString(),
            jahr: jahr.toString(),
            
            isLocked: false, drafts: unlockedDrafts,
            unternehmerData: { ...currentClientData.unternehmerData, durchschnittsumsatz: avgUnternehmerUmsatz, durchschnittsumsatzVJ: avgUnternehmerUmsatz },
            
            // 💉 CHIRURGIE-FIX: Gewaschene und bereinigte Personengesellschaft
            personengesellschaft: washedPersonengesellschaft,

            // --- DIE 4 OFFIZIELLEN VORJAHRES-CONTAINER ---
            vorjahresBwaData: vorjahresBwaData, 
            vorjahresUnternehmerData: vorjahresUnternehmerData,
            vorjahresPersonalData: vorjahresPersonalData,
            vorjahresCalcData: snapshotKurzauswertung, 
            // ----------------------------------------------

            bwaData: newBwaData, lohnfaktorData: resetLohnfaktor, budgetPlaner: { zeitraum: '', entries: [] },
            ulHalbjahr1: defaultULHalbjahr1(), ulHalbjahr2: defaultULHalbjahr2(), mitarbeiterLeistung: newMitarbeiterLeistung, gesamtleistungData: newGesamtleistungData, urlaubsverlustData: newUrlaubsverlustData, kalkulator: newKalkulator, geschaeftsjahre: updatedJahre,
            isCostAdjustmentRequired: false, isHJ1GesperrtGlobal: false, isHJ2Freigegeben: false,
        };

        delete newClientData.gesamtleistungLockedMetrics;
        delete newClientData.unternehmerData.lockedMetrics;

        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, past: newPast, future: [], isDirty: true, rolloverConfirm: false };
    }),

    updateYearBwaUrl: (jahr, bwaUrl) => set(state => {
        const newPast = getNewPast(state);
        const currentYears = state.clientData.geschaeftsjahre || [];
        const updatedJahre = currentYears.map((j: any) => j.jahr === jahr ? { ...j, bwaUrl } : j);
        const newClientData = { ...state.clientData, geschaeftsjahre: updatedJahre };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, past: newPast, future: [], isDirty: true };
    }),

    executeJahreswechsel: () => set((state) => {
        const newPast = getNewPast(state);
        const currentClientData = state.clientData;
        const currentYears = currentClientData.geschaeftsjahre || [];
        const latestYearNum = currentYears.length > 0 ? Math.max(...currentYears.map((j: any) => Number(j.jahr))) : new Date().getFullYear();

        const updatedJahre = currentYears.map((j: any) => {
            if (Number(j.jahr) === latestYearNum) return { ...j, status: 'abgeschlossen' };
            return j;
        });

        localStorage.setItem('CURRENT_YEAR_STATUS', 'abgeschlossen');
        window.dispatchEvent(new Event('storage')); 

        const lockedDrafts = safeClone(currentClientData.drafts || { existingStaff: {}, newStaff: [], entrepreneur: [], costs: [], clientMaster: [], openingHours: [] });
        const lockAll = (arr: any[]) => (arr || []).forEach(item => { if (item) { item.isLocked = true; item.lockedBy = 'System'; } });
        if (lockedDrafts.existingStaff) Object.values(lockedDrafts.existingStaff).forEach((item: any) => { if (item) { item.isLocked = true; } });
        lockAll(lockedDrafts.newStaff); lockAll(lockedDrafts.entrepreneur); lockAll(lockedDrafts.clientMaster); lockAll(lockedDrafts.costs); lockAll(lockedDrafts.openingHours);

        const newClientData = { ...currentClientData, isLocked: true, geschaeftsjahre: updatedJahre, drafts: lockedDrafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, past: newPast, future: [], isDirty: true, rolloverConfirm: true };
    }),
});